import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { SITE } from "@/config/site";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/category/xuanhuan", label: "分类" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    const cat = categories.find((c) => c.name.includes(kw));
    if (cat) {
      navigate(`/category/${cat.id}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(kw)}`);
    }
    setKeyword("");
    setSearchOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-950/90 backdrop-blur-md border-b border-background-200/40 py-3"
          : "bg-transparent py-5"
      }`}
    >
      {/* Progress bar */}
      <div
        className="absolute bottom-0 left-0 h-0.5 bg-primary-500 transition-all duration-150"
        style={{ width: `${progress}%` }}
      />

      <div className="mx-auto max-w-7xl px-4 md:px-6 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label="抖漫首页">
          <span className="relative w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center overflow-hidden">
            <i className="ri-play-circle-fill text-background-950 text-xl"></i>
          </span>
          <span className="font-heading text-xl md:text-2xl text-foreground-50">
            抖<span className="text-primary-400">漫</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-primary-400 font-medium"
                    : "text-foreground-300 hover:text-foreground-50"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          {/* 分类下拉 */}
          <div className="relative group">
            <button className="px-4 py-2 rounded-md text-sm whitespace-nowrap text-foreground-300 hover:text-foreground-50 flex items-center gap-1 cursor-pointer">
              分类
              <i className="ri-arrow-down-s-line text-xs"></i>
            </button>
            <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="w-56 rounded-xl bg-background-900 border border-background-200/40 p-2 shadow-2xl">
                {categories.map((c) => (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground-300 hover:text-foreground-50 hover:bg-background-800 transition-colors"
                  >
                    <i className={`${c.icon} text-primary-400`}></i>
                    <span>{c.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative hidden md:block">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="w-10 h-10 rounded-lg flex items-center justify-center text-foreground-300 hover:text-foreground-50 hover:bg-background-800 transition-colors cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            {searchOpen && (
              <form
                onSubmit={handleSearch}
                className="absolute right-0 top-12 w-72 p-2 rounded-xl bg-background-900 border border-background-200/40 flex items-center gap-2 shadow-2xl"
              >
                <i className="ri-search-line text-foreground-500 pl-2"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索作品或分类…"
                  className="flex-1 bg-transparent text-sm text-foreground-100 outline-none py-2 pr-2 placeholder:text-foreground-600"
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-md bg-primary-500 text-background-950 text-sm whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </form>
            )}
          </div>

          {/* CTA */}
          <Link
            to="/about"
            className="hidden md:inline-flex px-5 py-2 rounded-md bg-primary-500 text-background-950 text-sm font-medium whitespace-nowrap hover:bg-primary-400 transition-colors"
          >
            立即观看
          </Link>

          {/* Hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-10 h-10 rounded-lg flex items-center justify-center text-foreground-100 hover:bg-background-800 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-950/95 backdrop-blur-md border-t border-background-200/40">
          <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base ${
                    isActive
                      ? "text-primary-400 bg-background-800 font-medium"
                      : "text-foreground-300"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <div className="px-4 pt-2 pb-1 text-xs text-foreground-600">全部分类</div>
            <div className="grid grid-cols-2 gap-1 px-4 pb-3">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-300 hover:bg-background-800"
                >
                  <i className={`${c.icon} text-primary-400`}></i>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}